import { useRef, useMemo } from 'react';
import { useFrame, Canvas } from '@react-three/fiber';
import { jsxs, jsx } from 'react/jsx-runtime';
import { Stars, OrbitControls } from '@react-three/drei';
import { Object3D, Color } from 'three';

// src/realms/wilds/components/Tree.tsx
function Tree({ visual, onSelect }) {
  const ref = useRef(null);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    const phase = visual.position[0] * 0.4 + visual.position[2] * 0.6;
    const sway = Math.sin(clock.elapsedTime * 0.6 + phase) * 0.04;
    ref.current.rotation.z = sway;
  });
  const trunkHeight = 1.6 + visual.scale * 2.4;
  const trunkRadius = 0.09 + visual.scale * 0.055;
  const lightness = 52 + visual.intensity * 18;
  const canopyColor = `hsl(${visual.hue}, 82%, ${lightness}%)`;
  const trunkColor = "#6b5a45";
  const seed = Math.abs(Math.round(visual.position[0] * 127 + visual.position[2] * 311));
  const foliage = useMemo(() => {
    const clusters = [];
    const crownY = trunkHeight * 0.78;
    const baseR = 0.65 + visual.scale * 0.5 + visual.intensity * 0.3;
    clusters.push({ x: 0, y: crownY + baseR * 0.35, z: 0, r: baseR });
    clusters.push({ x: 0, y: crownY + baseR * 0.95, z: 0, r: baseR * 0.55 });
    const mainCount = 5 + Math.floor(visual.scale * 3);
    for (let i = 0; i < mainCount; i++) {
      const a = i / mainCount * Math.PI * 2 + seed % 100 * 0.063;
      const spread = baseR * (0.6 + (seed + i * 7) % 5 * 0.09);
      const yOff = ((seed + i * 13) % 7 - 3) * 0.1;
      const sz = baseR * (0.38 + (seed + i * 11) % 5 * 0.07);
      clusters.push({
        x: Math.cos(a) * spread,
        y: crownY + yOff,
        z: Math.sin(a) * spread,
        r: sz
      });
    }
    const lowerCount = 3 + Math.floor(visual.scale * 2);
    for (let i = 0; i < lowerCount; i++) {
      const a = i / lowerCount * Math.PI * 2 + seed % 50 * 0.126;
      const spread = baseR * 0.85 + 0.25;
      clusters.push({
        x: Math.cos(a) * spread,
        y: crownY - 0.35 - (seed + i * 17) % 4 * 0.12,
        z: Math.sin(a) * spread,
        r: baseR * (0.28 + (seed + i * 7) % 3 * 0.06)
      });
    }
    return clusters;
  }, [trunkHeight, visual.scale, visual.intensity, seed]);
  function handleClick(e) {
    if (!onSelect) return;
    e.stopPropagation();
    onSelect();
  }
  const onClick = onSelect ? handleClick : void 0;
  return /* @__PURE__ */ jsxs("group", { ref, position: visual.position, children: [
    /* @__PURE__ */ jsxs("mesh", { position: [0, trunkHeight / 2, 0], onClick, children: [
      /* @__PURE__ */ jsx("cylinderGeometry", { args: [trunkRadius * 0.65, trunkRadius, trunkHeight, 8] }),
      /* @__PURE__ */ jsx("meshStandardMaterial", { color: trunkColor, roughness: 0.9 })
    ] }),
    foliage.map((c, i) => /* @__PURE__ */ jsxs("mesh", { position: [c.x, c.y, c.z], onClick, children: [
      /* @__PURE__ */ jsx("dodecahedronGeometry", { args: [c.r, 1] }),
      /* @__PURE__ */ jsx("meshStandardMaterial", { color: canopyColor, roughness: 0.7 })
    ] }, i))
  ] });
}
function ForestBackdrop({
  count = 320,
  innerRadius = 36,
  outerRadius = 78,
  seed = 7
}) {
  const trunkRef = useRef(null);
  const canopy1Ref = useRef(null);
  const canopy2Ref = useRef(null);
  const canopy3Ref = useRef(null);
  const placements = useMemo(
    () => buildPlacements(count, innerRadius, outerRadius, seed),
    [count, innerRadius, outerRadius, seed]
  );
  useFrame(() => {
    const trunk = trunkRef.current;
    const c1 = canopy1Ref.current;
    const c2 = canopy2Ref.current;
    const c3 = canopy3Ref.current;
    if (!trunk || !c1 || !c2 || !c3) return;
    if (trunk.userData.placed) return;
    const dummy = new Object3D();
    const color = new Color();
    placements.forEach((p, i) => {
      const trunkH = p.trunkHeight;
      const canopyH = p.canopyHeight;
      const canopyR = p.canopyRadius;
      dummy.position.set(p.x, trunkH / 2, p.z);
      dummy.rotation.set(0, p.rot, 0);
      dummy.scale.set(p.trunkRadius * 2, trunkH, p.trunkRadius * 2);
      dummy.updateMatrix();
      trunk.setMatrixAt(i, dummy.matrix);
      dummy.position.set(p.x, trunkH + canopyH * 0.3, p.z);
      dummy.rotation.set(0, p.rot, 0);
      dummy.scale.set(canopyR * 0.9, canopyH * 0.5, canopyR * 0.9);
      dummy.updateMatrix();
      c1.setMatrixAt(i, dummy.matrix);
      const off2x = Math.cos(p.rot + 0.8) * canopyR * 0.35;
      const off2z = Math.sin(p.rot + 0.8) * canopyR * 0.35;
      dummy.position.set(p.x + off2x, trunkH + canopyH * 0.5, p.z + off2z);
      dummy.rotation.set(0, p.rot * 1.3, 0);
      dummy.scale.set(canopyR * 0.7, canopyH * 0.4, canopyR * 0.7);
      dummy.updateMatrix();
      c2.setMatrixAt(i, dummy.matrix);
      const off3x = Math.cos(p.rot + 3.2) * canopyR * 0.4;
      const off3z = Math.sin(p.rot + 3.2) * canopyR * 0.4;
      dummy.position.set(p.x + off3x, trunkH + canopyH * 0.1, p.z + off3z);
      dummy.rotation.set(0, p.rot * 0.7, 0);
      dummy.scale.set(canopyR * 0.55, canopyH * 0.35, canopyR * 0.55);
      dummy.updateMatrix();
      c3.setMatrixAt(i, dummy.matrix);
      const hue = BACKDROP_HUES[i % BACKDROP_HUES.length];
      color.setHSL(hue / 360, 0.5, 0.36);
      c1.setColorAt(i, color);
      color.setHSL(hue / 360, 0.5, 0.4);
      c2.setColorAt(i, color);
      color.setHSL(hue / 360, 0.5, 0.32);
      c3.setColorAt(i, color);
    });
    trunk.instanceMatrix.needsUpdate = true;
    c1.instanceMatrix.needsUpdate = true;
    c2.instanceMatrix.needsUpdate = true;
    c3.instanceMatrix.needsUpdate = true;
    if (c1.instanceColor) c1.instanceColor.needsUpdate = true;
    if (c2.instanceColor) c2.instanceColor.needsUpdate = true;
    if (c3.instanceColor) c3.instanceColor.needsUpdate = true;
    trunk.userData.placed = true;
  });
  return /* @__PURE__ */ jsxs("group", { children: [
    /* @__PURE__ */ jsxs("instancedMesh", { ref: trunkRef, args: [void 0, void 0, count], children: [
      /* @__PURE__ */ jsx("cylinderGeometry", { args: [1, 1, 1, 6] }),
      /* @__PURE__ */ jsx("meshStandardMaterial", { color: "#5a4a38", roughness: 0.95 })
    ] }),
    /* @__PURE__ */ jsxs("instancedMesh", { ref: canopy1Ref, args: [void 0, void 0, count], children: [
      /* @__PURE__ */ jsx("dodecahedronGeometry", { args: [1, 1] }),
      /* @__PURE__ */ jsx("meshStandardMaterial", { color: "#ffffff", roughness: 0.8 })
    ] }),
    /* @__PURE__ */ jsxs("instancedMesh", { ref: canopy2Ref, args: [void 0, void 0, count], children: [
      /* @__PURE__ */ jsx("dodecahedronGeometry", { args: [1, 1] }),
      /* @__PURE__ */ jsx("meshStandardMaterial", { color: "#ffffff", roughness: 0.8 })
    ] }),
    /* @__PURE__ */ jsxs("instancedMesh", { ref: canopy3Ref, args: [void 0, void 0, count], children: [
      /* @__PURE__ */ jsx("dodecahedronGeometry", { args: [1, 1] }),
      /* @__PURE__ */ jsx("meshStandardMaterial", { color: "#ffffff", roughness: 0.8 })
    ] })
  ] });
}
var BACKDROP_HUES = [120, 140, 100, 160, 80, 150, 95, 130, 110, 170];
function buildPlacements(count, innerRadius, outerRadius, seed) {
  const random = mulberry32(seed);
  const out = [];
  for (let i = 0; i < count; i++) {
    const t = Math.sqrt(random());
    const r = innerRadius + t * (outerRadius - innerRadius);
    const theta = random() * Math.PI * 2;
    const x = Math.cos(theta) * r;
    const z = Math.sin(theta) * r;
    const sizeJitter = 0.7 + random() * 0.9;
    const trunkHeight = 1.6 * sizeJitter;
    const trunkRadius = 0.18 * sizeJitter;
    const canopyHeight = 2.4 * sizeJitter;
    const canopyRadius = 1.1 * sizeJitter;
    out.push({
      x,
      z,
      rot: random() * Math.PI * 2,
      trunkHeight,
      trunkRadius,
      canopyHeight,
      canopyRadius
    });
  }
  return out;
}
function mulberry32(seed) {
  let s = seed >>> 0;
  return () => {
    s = s + 1831565813 >>> 0;
    let t = s;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
var SKY = "#0e1030";
var GROUND_COLOR = "#2a5a35";
function WildsWorld({ children, dpr = [1, 2] }) {
  return /* @__PURE__ */ jsxs(
    Canvas,
    {
      camera: { position: [0, 8, 36], fov: 60, near: 0.1, far: 500 },
      dpr,
      gl: { antialias: true },
      children: [
        /* @__PURE__ */ jsx("color", { attach: "background", args: [SKY] }),
        /* @__PURE__ */ jsx("fog", { attach: "fog", args: [SKY, 70, 180] }),
        /* @__PURE__ */ jsx(
          Stars,
          {
            radius: 180,
            depth: 80,
            count: 2500,
            factor: 4,
            saturation: 0.5,
            fade: true,
            speed: 0.8
          }
        ),
        /* @__PURE__ */ jsx("ambientLight", { intensity: 0.65 }),
        /* @__PURE__ */ jsx("hemisphereLight", { args: ["#f0f8ff", "#3a5a38", 1.2] }),
        /* @__PURE__ */ jsx("directionalLight", { position: [10, 30, 8], intensity: 1.5, color: "#fff5e0" }),
        /* @__PURE__ */ jsx(Ground, {}),
        /* @__PURE__ */ jsx(ForestBackdrop, {}),
        /* @__PURE__ */ jsx(
          OrbitControls,
          {
            enablePan: false,
            maxDistance: 80,
            minDistance: 6,
            maxPolarAngle: Math.PI / 2.05
          }
        ),
        children
      ]
    }
  );
}
function Ground() {
  return /* @__PURE__ */ jsxs("mesh", { rotation: [-Math.PI / 2, 0, 0], position: [0, -0.01, 0], receiveShadow: true, children: [
    /* @__PURE__ */ jsx("circleGeometry", { args: [120, 64] }),
    /* @__PURE__ */ jsx("meshStandardMaterial", { color: GROUND_COLOR, roughness: 0.9 })
  ] });
}

// src/realms/wilds/index.ts
var GENRE_HUE = {
  drama: 70,
  // oak — warm green-gold
  thriller: 150,
  // pine — cool green
  horror: 25,
  // dead/gnarled — burnt umber
  comedy: 95,
  // birch — bright spring
  romance: 330,
  // cherry blossom — pink
  scifi: 180,
  // alien luminescent
  documentary: 110,
  // generic forest green
  animation: 280
  // fantasy violet
};
function clamp01(n) {
  return Math.max(0, Math.min(1, n));
}
function popularityToIntensity(film) {
  const watches = Math.max(0, film.watch_starts_count);
  const log = Math.log10(1 + watches);
  return clamp01(log / 6);
}
function deriveScale(film) {
  const scenes = film.scenes_count ?? 1;
  return clamp01(0.3 + Math.log10(1 + scenes) / 3);
}
function deriveHue(film) {
  const genreKey = (film.genre ?? "").toLowerCase().replace(/[^a-z]/g, "");
  const hue = GENRE_HUE[genreKey];
  return hue !== void 0 ? hue : 110;
}
function deriveState(film) {
  return film.published ? "default" : "new";
}
var wildsMapping = (film) => ({
  position: [0, 0, 0],
  // assigned by the realm's spatial layouter
  scale: deriveScale(film),
  hue: deriveHue(film),
  intensity: popularityToIntensity(film),
  state: deriveState(film),
  metadata: {
    title: film.title,
    popularity: film.watch_starts_count,
    genre: film.genre
  }
});

// src/realms/wilds/components/layout.ts
function distributeForest(count, options = {}) {
  const radius = options.radius ?? 28;
  const clumpSpread = options.clumpSpread ?? 4;
  const random = options.seed !== void 0 ? mulberry322(options.seed) : Math.random;
  const desiredClumps = options.clumpCount ?? Math.max(2, Math.round(count / 4));
  const clumpCount = Math.max(1, Math.min(count, desiredClumps));
  const anchors = [];
  for (let i = 0; i < clumpCount; i++) {
    const r = Math.sqrt(random()) * radius;
    const theta = random() * Math.PI * 2;
    anchors.push([Math.cos(theta) * r, 0, Math.sin(theta) * r]);
  }
  const positions = [];
  for (let i = 0; i < count; i++) {
    const anchor = anchors[i % clumpCount];
    const dx = gaussian(random) * clumpSpread;
    const dz = gaussian(random) * clumpSpread;
    positions.push([anchor[0] + dx, 0, anchor[2] + dz]);
  }
  return positions;
}
function gaussian(random) {
  const u1 = Math.max(1e-9, random());
  const u2 = random();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}
function mulberry322(seed) {
  let s = seed >>> 0;
  return () => {
    s = s + 1831565813 >>> 0;
    let t = s;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
function WildsSandbox({
  films,
  onFilmSelect,
  seed = 1
}) {
  const placed = useMemo(() => {
    const positions = distributeForest(films.length, { seed });
    return films.map((film, i) => ({
      film,
      visual: { ...wildsMapping(film), position: positions[i] }
    }));
  }, [films, seed]);
  return /* @__PURE__ */ jsx(WildsWorld, { children: placed.map(({ film, visual }) => /* @__PURE__ */ jsx(
    Tree,
    {
      visual,
      onSelect: onFilmSelect ? () => onFilmSelect(film) : void 0
    },
    film.id
  )) });
}

export { ForestBackdrop, Tree, WildsSandbox, WildsWorld, distributeForest };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map